import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { loadConnectionSettings } from "../services/connectionSettingsApi";
import { fetchCurrentUser } from "../services/authApi";
import type { PmsProviderType } from "../services/pmsApi";
import { isUiSkin, isUiSkinPreference, resolveSkin, type UiSkin, type UiSkinPreference } from "../theme/skins";

const STORAGE_KEY = "optivo-ui-skin";

interface SkinContextValue {
    skin: UiSkin;
    preference: UiSkinPreference;
    /**
     * Paints a preference a page has already saved for `connectionId`, and
     * saves nothing: the page saves it, naming the property it read
     * (`AppearanceCard`). Painted at once while the app shows that property,
     * and ignored once it shows another. Where it cannot tell — a read of its
     * own is still out, or either side names no property — the provider reads
     * the open property's skin again.
     */
    applyPreference: (preference: UiSkinPreference, connectionId: number | undefined) => void;
    loading: boolean;
    /** Re-fetches connection settings + active provider (e.g. after switching PMS connection). */
    refresh: () => void;
}

const SkinContext = createContext<SkinContextValue | undefined>(undefined);

function readCachedSkin(): UiSkin | null {
    try {
        const cached = window.localStorage.getItem(STORAGE_KEY);
        return cached && isUiSkin(cached) ? cached : null;
    } catch {
        return null;
    }
}

function applySkin(skin: UiSkin) {
    document.documentElement.dataset.skin = skin;
    try {
        window.localStorage.setItem(STORAGE_KEY, skin);
    } catch {
        // Storage unavailable (private browsing, quota) — the DOM attribute still applies.
    }
}

export function SkinProvider({ children }: { children: ReactNode }) {
    const [preference, setPreferenceState] = useState<UiSkinPreference>("auto");
    const [providerType, setProviderType] = useState<PmsProviderType | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshToken, setRefreshToken] = useState(0);
    const [skin, setSkin] = useState<UiSkin>(() => readCachedSkin() ?? "optivo");
    // Guards the reactive resolve effect below from re-running (redundantly) during initial load,
    // where the fetch effect already resolves and applies the skin synchronously.
    const initializedRef = useRef(false);
    // The property the painted preference was read for. Unknown while a read is
    // out, and when the last one named none.
    const paintedConnectionRef = useRef<number | undefined>(undefined);

    // Paint the last-known skin immediately, before the network round-trip below resolves.
    useEffect(() => {
        const cached = readCachedSkin();
        if (cached) document.documentElement.dataset.skin = cached;
    }, []);

    useEffect(() => {
        let cancelled = false;
        paintedConnectionRef.current = undefined;
        setLoading(true);
        Promise.all([
            loadConnectionSettings().catch(() => null),
            fetchCurrentUser().catch(() => null),
        ]).then(([loaded, user]) => {
            if (cancelled) return;
            const settings = loaded?.settings;
            const pref = settings?.uiTheme && isUiSkinPreference(settings.uiTheme)
                ? settings.uiTheme
                : "auto";
            const provider = user?.activeProviderType ?? null;
            const resolved = resolveSkin({ preference: pref, providerType: provider });

            paintedConnectionRef.current = loaded?.connectionId;
            setPreferenceState(pref);
            setProviderType(provider);
            setSkin(resolved);
            applySkin(resolved);
            initializedRef.current = true;
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [refreshToken]);

    // Reacts to preference/provider changes made after the initial load (e.g. applyPreference, or a
    // PMS connection switch that updates providerType via refresh()).
    useEffect(() => {
        if (!initializedRef.current) return;
        const resolved = resolveSkin({ preference, providerType });
        setSkin(resolved);
        applySkin(resolved);
    }, [preference, providerType]);

    const refresh = useCallback(() => setRefreshToken((v) => v + 1), []);

    const applyPreference = useCallback(
        (next: UiSkinPreference, connectionId: number | undefined) => {
            const painted = paintedConnectionRef.current;
            if (painted === undefined || connectionId === undefined) {
                // A read still out may have been sent before the save, and a
                // property nobody named cannot be matched: read it again.
                refresh();
            } else if (painted === connectionId) {
                setPreferenceState(next);
            }
            // Otherwise the save was for a property the app no longer shows.
        },
        [refresh],
    );

    return (
        <SkinContext.Provider value={{ skin, preference, applyPreference, loading, refresh }}>
            {children}
        </SkinContext.Provider>
    );
}

export function useSkin(): SkinContextValue {
    const ctx = useContext(SkinContext);
    if (ctx === undefined) throw new Error("useSkin must be used within a SkinProvider");
    return ctx;
}
